import type { components, paths } from '@api/autogenerated-api'
import type { ComponentDefinitionBlockTypes } from '@api/component-definitions/schema'
import type { Dictionary, Maybe } from '@core/utils/types'
import type {
  UseQueryOptions,
  UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import type { AxiosError } from 'axios'

/** A schema from the backend OpenAPI spec (`bun api-ts` regenerates it). */
export type ApiSchema<Name extends keyof components['schemas']> =
  components['schemas'][Name]

type ApiOperation<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = NonNullable<paths[Path][Method]>

type UnknownIfNever<T> = [T] extends [never] ? unknown : T

/** URL path params of an operation, named exactly as in the backend spec (e.g. `{ workspace_id: string }`). */
export type ApiPathParams<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = UnknownIfNever<
  ApiOperation<Path, Method> extends { parameters: { path: infer P } }
    ? P
    : never
>

/** Form-urlencoded request body of an operation (the counterpart of `ApiRequestBody`, which is JSON only). */
export type ApiFormRequestBody<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = ApiOperation<Path, Method> extends {
  requestBody: { content: { 'application/x-www-form-urlencoded': infer B } }
}
  ? B
  : never

/** Query params of an operation, as named in the backend spec. */
export type ApiQueryParams<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = UnknownIfNever<
  ApiOperation<Path, Method> extends { parameters: { query?: infer Q } }
    ? NonNullable<Q>
    : never
>

/** Path params and query params of an operation as one flat params object. */
export type ApiParams<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = ApiPathParams<Path, Method> & ApiQueryParams<Path, Method>

/** JSON request body of an operation. */
export type ApiRequestBody<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = ApiOperation<Path, Method> extends { requestBody?: infer R }
  ? R extends { content: { 'application/json': infer B } }
    ? B
    : never
  : never

type SuccessStatus = 200 | 201 | 202 | 204

/** Success status codes an operation declares in the spec. */
type ApiSuccessCode<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = ApiOperation<Path, Method> extends { responses: infer R }
  ? keyof R & SuccessStatus
  : never

/**
 * JSON response body of an operation for the given success code; `void` for 204. The code is required, and one the
 * operation does not declare is a type error.
 */
export type ApiReturnType<
  Path extends keyof paths,
  Method extends keyof paths[Path],
  Code extends ApiSuccessCode<Path, Method>,
> = Code extends 204
  ? // biome-ignore lint/suspicious/noConfusingVoidType: This is a return value and as such `void` fits better
    void
  : ApiOperation<Path, Method> extends {
        responses: {
          [C in Code]: { content: { 'application/json': infer JSON } }
        }
      }
    ? JSON
    : never

/**
 * Page-based query params: the endpoint converts `page`/`pageSize` into the backend's
 * `limit`/`offset` before the request.
 */
export type WithPagination<Query> = Omit<Query, 'limit' | 'offset'> & {
  page?: number
  pageSize?: number
}

export type ErrorResponse = ApiSchema<'ProblemDetails'>

export type ApiErrorCode = ErrorResponse['code']

/**
 * What a caller may override on an endpoint's query: everything but the key and the fetch function, which the endpoint
 * owns. `TSelected` is what `select` returns.
 */
export type ApiQueryOptions<
  TData,
  TSelected = TData,
  TError = AxiosError<ErrorResponse>,
> = Omit<UseQueryOptions<TData, TError, TSelected>, 'queryKey' | 'queryFn'>

export type ApiSuspenseQueryOptions<
  TData,
  TSelected = TData,
  TError = AxiosError<ErrorResponse>,
> = Omit<
  UseSuspenseQueryOptions<TData, TError, TSelected>,
  'queryKey' | 'queryFn'
>

export type SearchRequest = {
  limit?: number
  offset?: number
  docType?: ComponentDefinitionBlockTypes
}

export enum UserRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  USER = 'USER',
}

export type SearchResponse<T> = {
  totalCount: number
  results: T[]
}

export const CriticalityWeights = {
  NOT_IMPORTANT: 0,
  NORMAL: 1,
  IMPORTANT: 2,
  VERY_IMPORTANT: 3,
} as const

export type Criticality = keyof typeof CriticalityWeights
export type CriticalityWeight = (typeof CriticalityWeights)[Criticality]

export const ImportanceWeights = {
  HIGH: 2,
  MEDIUM: 1,
  LOW: 0,
} as const

export type Importance = keyof typeof ImportanceWeights
export type ImportanceWeight = (typeof ImportanceWeights)[Importance]

export type OrderBy<T extends string> = T | `-${T}`

export type MetadataValueEnum = string[] | number[] | boolean[] | null

export interface MetaTypeKind {
  type: 'number' | 'string' | 'datetime' | 'enum'
  valueIn: MetadataValueEnum
}

export type MetadataDict = Dictionary<MetaTypeKind>

export type MetadataContext = {
  workspaceMetadata: Maybe<MetadataDict>
}

export type MutateMessageOptions = {
  successMessage?: string
  errorMessage?: string
}
